/**
 * Typed env loader. Validates `import.meta.env.VITE_*` at module init.
 *
 * Spec: docs/architecture/data-layer-spec.md §10
 *       docs/architecture/data-layer-slicing-plan.md §2
 *
 * Rules:
 * - `VITE_DATA_ADAPTER` defaults to `"mock"` so dev/test work with no env file.
 * - When `VITE_DATA_ADAPTER === "supabase"`, `VITE_SUPABASE_URL` +
 *   `VITE_SUPABASE_ANON_KEY` become required (validated by zod).
 * - `loadEnv()` THROWS on invalid env. `main.tsx` is the boot-time catch site.
 */
import { z } from "zod";

const adapterSchema = z.enum(["mock", "supabase"]).default("mock");
const flagSchema = z.enum(["true", "false"]).default("false");

const baseSchema = z.object({
  VITE_DATA_ADAPTER: adapterSchema,
  VITE_FLAG_AUTH_ENABLED: flagSchema,
  VITE_FLAG_SUPABASE_ENABLED: flagSchema,
  VITE_ENABLE_MSW_IN_PROD: flagSchema,
  VITE_SUPABASE_URL: z.string().url().optional(),
  VITE_SUPABASE_ANON_KEY: z.string().min(20).optional(),
  /** Clerk publishable key — required when VITE_FLAG_AUTH_ENABLED=true */
  VITE_CLERK_PUBLISHABLE_KEY: z.string().startsWith("pk_").optional(),
});

export type Env = z.infer<typeof baseSchema>;

export interface EnvSource {
  [key: string]: unknown;
}

function refineAuth(value: Env): { ok: true } | { ok: false; issues: string[] } {
  const authEnabled = value.VITE_FLAG_AUTH_ENABLED === "true";
  const supabaseRequired =
    value.VITE_DATA_ADAPTER === "supabase" ||
    authEnabled ||
    value.VITE_FLAG_SUPABASE_ENABLED === "true";

  if (!supabaseRequired) return { ok: true };

  const issues: string[] = [];
  if (!value.VITE_SUPABASE_URL) {
    issues.push(
      "VITE_SUPABASE_URL: required when VITE_DATA_ADAPTER=supabase or VITE_FLAG_AUTH_ENABLED/VITE_FLAG_SUPABASE_ENABLED=true",
    );
  }
  if (!value.VITE_SUPABASE_ANON_KEY) {
    issues.push(
      "VITE_SUPABASE_ANON_KEY: required when VITE_DATA_ADAPTER=supabase or VITE_FLAG_AUTH_ENABLED/VITE_FLAG_SUPABASE_ENABLED=true",
    );
  }
  if (authEnabled && !value.VITE_CLERK_PUBLISHABLE_KEY) {
    issues.push(
      "VITE_CLERK_PUBLISHABLE_KEY: required when VITE_FLAG_AUTH_ENABLED=true",
    );
  }
  return issues.length ? { ok: false, issues } : { ok: true };
}

export function parseEnv(source: EnvSource): Env {
  const parsed = baseSchema.safeParse(source);
  if (!parsed.success) {
    const issues = parsed.error.issues.map(
      (i) => ` - ${i.path.join(".") || "(root)"}: ${i.message}`,
    );
    throw new Error(
      "Invalid environment configuration:\n" + issues.join("\n"),
    );
  }
  const refined = refineAuth(parsed.data);
  if (!refined.ok) {
    throw new Error(
      "Invalid environment configuration:\n" +
        refined.issues.map((i) => " - " + i).join("\n"),
    );
  }
  return parsed.data;
}

let cached: Env | undefined;

/** Load + cache env from `import.meta.env`. Throws on invalid. */
export function loadEnv(): Env {
  if (cached) return cached;
  cached = parseEnv(import.meta.env as unknown as EnvSource);
  return cached;
}

/** Test-only — reset memoization between cases. */
export const __envTesting__ = {
  reset(): void {
    cached = undefined;
  },
};
